import { useState, useCallback, useEffect, useRef } from "react";
import { localStore } from "../utils/safeStorage";

const GEO_CACHE_KEY = "sendly-geo-cache";
const GEO_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

function getCachedPosition() {
  const cached = localStore.getJSON(GEO_CACHE_KEY);
  if (cached && Date.now() - cached.ts < GEO_CACHE_TTL) return cached;
  return null;
}

function cachePosition(latitude, longitude) {
  localStore.setJSON(GEO_CACHE_KEY, { latitude, longitude, ts: Date.now() });
}

/**
 * Browser geolocation hook with caching.
 * Only calls getCurrentPosition when:
 *   1. User clicks "Nearby" button (requestLocation), OR
 *   2. Permission is already "granted" AND no valid cache exists (silent auto-request)
 *
 * This prevents iOS Safari from showing the GPS permission popup on every
 * component remount (e.g. navigating back from gym to gym selector).
 * Caches position in localStorage for 10 minutes.
 */
export function useGeolocation({ autoRequest = false } = {}) {
  const cached = getCachedPosition();
  const [position, setPosition] = useState({
    latitude: cached?.latitude || null,
    longitude: cached?.longitude || null,
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchPosition = useCallback(() => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported");
      return;
    }
    setLoading(true);
    setError(null);
    // Try high accuracy first (GPS on mobile), fall back to low accuracy
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (!mountedRef.current) return;
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setPosition({ latitude: lat, longitude: lng });
        cachePosition(lat, lng);
        setLoading(false);
      },
      (highAccErr) => {
        if (!mountedRef.current) return;
        // Fallback: try without high accuracy (WiFi/cell tower)
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (!mountedRef.current) return;
            const lat = pos.coords.latitude;
            const lng = pos.coords.longitude;
            setPosition({ latitude: lat, longitude: lng });
            cachePosition(lat, lng);
            setLoading(false);
          },
          (err) => {
            if (!mountedRef.current) return;
            setError(err.message);
            setLoading(false);
          },
          { enableHighAccuracy: false, timeout: 15000, maximumAge: 600000 }
        );
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 300000 }
    );
  }, []);

  // Explicit request — always calls getCurrentPosition (user clicked button)
  const requestLocation = useCallback(() => {
    fetchPosition();
  }, [fetchPosition]);

  // Auto-request on mount ONLY if permission is already granted (no popup).
  // Uses the Permissions API to check without triggering a prompt.
  // Falls back to using cache-only if Permissions API is unavailable.
  useEffect(() => {
    if (position.latitude) return; // already have a position from cache
    if (!autoRequest) return;
    if (!navigator.permissions) {
      // Permissions API not available (older iOS) — skip auto-request to avoid popup
      return;
    }
    navigator.permissions.query({ name: "geolocation" }).then((status) => {
      if (!mountedRef.current) return;
      if (status.state === "granted") {
        // Permission already granted — safe to call without popup
        fetchPosition();
      }
      // "prompt" or "denied" — don't auto-request, wait for user button click
    }).catch(() => {
      // Permissions API query failed — skip auto-request
    });
  }, []); // intentionally run only on mount

  return { ...position, error, loading, requestLocation };
}
